import { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from '../../../../ui/toast';
import { useEditJobOpeningMutation } from '../../../../utils/api_service/jobOpeningsApi';
import { jobOpeningPages, jobOpeningTypes } from '../../../../utils/types/JobOpenings';
import {
  DEFAULT_STAGES,
  PAGE_ELEMENT_MAP,
  STATUS_KEY_TO_LABEL,
  TAB_PAGE_LABELS,
} from '../constants';
import { getPageStatus } from '../functions';
import { IStageConfig, ITabStep, PageStatus } from '../interfaces';
import { useJobOpeningData } from './useJobOpeningData';
import { useJobOpeningForm } from './useJobOpeningForm';
import { useJobOpeningNavigation } from './useJobOpeningNavigation';

export const useCreateJobOpening = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [show, setShow] = useState<boolean>(true);

  // Data hook
  const {
    jobData,
    isLoading: dataLoading,
    jobTitle,
    jobOpeningElements,
    completedTabs,
    tabCounts,
    refetch,
  } = useJobOpeningData();

  // Navigation hook
  const { currentPage, currentSubTab, navigateToTab, navigateToSubTab } = useJobOpeningNavigation();

  // Form state hook
  const formState = useJobOpeningForm(jobData, id);

  // RTK mutation for editing job opening elements
  const [editJobOpening] = useEditJobOpeningMutation();

  // Dialog state
  const [modalState, setModalState] = useState<boolean>(!id || id.trim() === '');
  const [confirmDialogState, setConfirmDialogState] = useState(false);

  // Page status
  const [pageStatus, setPageStatus] = useState<PageStatus>('loading');
  const [requiredTabs, setRequiredTabs] = useState<string[]>([]);

  // Local job title for display
  const [localJobTitle, setLocalJobTitle] = useState<string>('');

  // Stages for progress tracker
  const [stages, setStages] = useState<IStageConfig[]>(DEFAULT_STAGES);

  // Sync job title from server data
  useEffect(() => {
    if (jobTitle) {
      setLocalJobTitle(jobTitle);
    }
  }, [jobTitle]);

  // Sync stages from jobData status
  useEffect(() => {
    if (jobData?.status) {
      const statusUpdate = jobData.status;
      setStages((prevStages) =>
        prevStages.map((stage) => {
          const matchKey = Object.keys(STATUS_KEY_TO_LABEL).find(
            (key) => STATUS_KEY_TO_LABEL[key] === stage.label,
          );
          if (!matchKey) return stage;
          const newStatus = statusUpdate[matchKey as keyof typeof statusUpdate];
          if (!newStatus) return stage;
          const newSubIndex = stage.subStages.findIndex(
            (sub) =>
              sub.label.toLowerCase() === newStatus.toLowerCase() ||
              sub.status.toLowerCase() === newStatus.toLowerCase(),
          );
          return {
            ...stage,
            status: newStatus,
            currentSubStageIndex: newSubIndex >= 0 ? newSubIndex : stage.currentSubStageIndex,
          };
        }),
      );
    }
  }, [jobData]);

  // Page status logic - using extracted function
  useEffect(() => {
    const result = getPageStatus(currentPage, jobOpeningElements, completedTabs, dataLoading);
    setPageStatus(result.status);
    setRequiredTabs(result.requiredTabs);
  }, [currentPage, jobOpeningElements, completedTabs, dataLoading]);

  // Refetch data when navigating to Job Opening Details
  useEffect(() => {
    if (id && currentPage === 'Job Opening Details' && typeof refetch === 'function') {
      refetch();
    }
  }, [id, currentPage, refetch]);

  // Compute tab steps
  const steps: ITabStep[] = useMemo(() => {
    return TAB_PAGE_LABELS.map((label) => ({
      label,
      status:
        label === currentPage
          ? 'active'
          : jobOpeningElements.includes(label)
            ? 'non-active'
            : 'disabled',
    }));
  }, [currentPage, jobOpeningElements]);

  // Enable an element for the current page (RTK mutation)
  const enableElement = async () => {
    try {
      const elements = Array.from(
        new Set([
          ...jobOpeningElements,
          PAGE_ELEMENT_MAP[currentPage] as jobOpeningTypes,
        ]),
      );
      await editJobOpening({ id, elements }).unwrap();
    } catch {
      toast.error('Error Updating Job Opening');
    }
  };

  // Navigate away on modal close confirmation
  const handleModalToggle = () => {
    setModalState(false);
    history.push('/job-openings-list');
    setConfirmDialogState(false);
  };

  return {
    // Form state (spread)
    ...formState,

    // Job info
    jobTitleLabel: localJobTitle,
    setJobTitleLabel: setLocalJobTitle,
    jobDescription: formState.jobDescription,

    // Dialog state
    modalState,
    setModalState,
    confirmDialogState,
    setConfirmDialogState,

    // Navigation
    steps,
    currentPage,
    currentSubTab,
    setCurrentPage: navigateToTab,
    navigateToSubTab,

    // Page status
    pageStatus,
    requiredTabs,
    enableElement,
    dataLoading,

    // Modal toggle
    handleModalToggle,

    // Progress tracker
    stages,
    setStages,

    // Elements & tabs
    jobOpeningElements,
    setJobOpeningElements: () => {},
    completedJobOpeningDetails: completedTabs,
    setCompletedJobOpeningDetails: () => {},

    // Progress tracker visibility
    show,
    setShow,

    // Tab counts
    tabCounts,

    // Job ID
    jobOpeningId: id,
  };
};
